import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { http } from '../../shared/api/http'
import { useOrderCounters, CATEGORY_CONFIG, type OrderCategory } from '../../shared/store/ordersStore'
import { CloudDownload, Share2, Mail, EllipsisVertical, ArrowLeft, Eye } from 'lucide-react'
import { getFileIcon, canPreviewFile } from '../../shared/utils/fileUtils'
import { getAvailableOrderActions, getActionButtonClasses } from '../../shared/utils/orderActions'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen'
import 'yet-another-react-lightbox/styles.css'
import { ProfileDropdown } from '../../shared/components/ProfileDropdown'
import { NotificationBell } from '../../shared/components/NotificationBell'
import FloatingNavigation from '../../shared/components/FloatingNavigation'
import { EmailModal } from '../../shared/components/EmailModal'
import SubmittedPriceInfo from '../../shared/components/SubmittedPriceInfo'
import ConfirmationCard from '../../shared/components/ConfirmationCard'
import { logger } from '../../shared/utils/logger'


type FileItem = { 
  name: string
  size?: string
  type?: string
}

type Props = {
  title: string
  previewUrl?: string
  details: React.ReactNode[]
  processingTypes: string[]
  files: FileItem[]
  quantity?: number
  status?: string
  orderId: string
  // Информация о поданной цене
  submittedPrice?: number
  submittedDeliveryTime?: number
  submittedComment?: string
  priceFormatted?: string
  deliveryTimeFormatted?: string
  // Информация об отказе
  rejectedReason?: string
  rejectedAt?: string
  rejectedCompanyName?: string
  // Информация о подтверждении
  confirmationState?: 'pending' | 'accepted' | 'declined' | 'modified' | null
  showConfirmationCard?: boolean
  // Информация о дедлайне
  deadlineFormatted?: string
  deadlineStatus?: string
  deadlineCssClass?: string
  daysRemaining?: number
  // actions
  onDownloadFile?: (filename: string) => void
  onDownloadAll?: () => void
  onTake?: () => void
  onReject?: () => void
  onRestore?: () => void
  onSubmitPrice?: () => void
  onEditPrice?: () => void
  onComplete?: () => void
  onDefect?: () => void
  onFix?: () => void
  onBack?: () => void
  onPreviewFile?: (filename: string) => void
  // Обработчики подтверждения
  onAcceptConfirmation?: () => void
  onRequestPriceChange?: () => void
}

const getStatusColor = (status: string) => {
  const colors: Record<string, { bg: string; text: string }> = {
    new: { bg: '#dcfce7', text: '#166534' },
    watching: { bg: '#e0f2fe', text: '#0c4a6e' },
    calculating: { bg: '#fef3c7', text: '#92400e' },
    price_given: { bg: '#dbeafe', text: '#1e40af' },
    pending_confirmation: { bg: '#fed7aa', text: '#c2410c' }, // Оранжевый цвет для "Ждет подтверждения"
    executing: { bg: '#f3e8ff', text: '#6b21a8' },
    defect: { bg: '#fee2e2', text: '#991b1b' },
    rejected: { bg: '#f3f4f6', text: '#374151' },
    archived: { bg: '#f3f4f6', text: '#374151' },
    completed: { bg: '#dcfce7', text: '#166534' }
  }
  return colors[status] || { bg: '#f3f4f6', text: '#374151' }
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    new: 'Новый',
    watching: 'На просмотре',
    calculating: 'На просчете',
    price_given: 'Оценен',
    pending_confirmation: 'Ждет подтверждения',
    executing: 'Выполняется',
    defect: 'Брак',
    rejected: 'Отказ',
    archived: 'Архив',
    completed: 'Выполнен'
  }
  return labels[status] || status
}



export const OrderDetailDesktop: React.FC<Props> = ({
  title,
  previewUrl,
  details,
  processingTypes,
  files,
  quantity,
  status = 'new',
  orderId,
  submittedPrice,
  submittedDeliveryTime,
  submittedComment,
  priceFormatted,
  deliveryTimeFormatted,
  rejectedReason,
  rejectedAt,
  rejectedCompanyName,
  confirmationState,
  showConfirmationCard,
  // Информация о дедлайне
  deadlineFormatted,
  deadlineStatus,
  deadlineCssClass,
  daysRemaining,
  onDownloadFile,
  onDownloadAll,
  onTake,
  onReject,
  onRestore,
  onSubmitPrice,
  onEditPrice,
  onComplete,
  onDefect,
  onFix,
  onBack,
  onPreviewFile,
  onAcceptConfirmation,
  onRequestPriceChange,
}) => {
  const navigate = useNavigate()
  const [showFileActions, setShowFileActions] = useState<string | null>(null)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [emailModal, setEmailModal] = useState<{
    isOpen: boolean
    filename?: string
    filesCount?: number
  }>({
    isOpen: false
  })
  const statusStyle = getStatusColor(status)

  // Используем хук для загрузки счетчиков из глобального кеша напрямую
  const { counts } = useOrderCounters()

  const handleEmailSingle = (filename: string) => {
    setEmailModal({
      isOpen: true,
      filename,
    })
    setShowFileActions(null)
  }

  const handleEmailAll = () => {
    setEmailModal({
      isOpen: true,
      filesCount: files.length
    })
  }

  const closeEmailModal = () => {
    setEmailModal({ isOpen: false })
  }

  // Данные для плавающей навигации - переход на главную страницу
  const totalCatalogCount = counts.new
  const totalReviewsCount = counts.watching + counts.calculating + counts.price_given
  const totalWorkCount = counts.executing + counts.defect
  const totalArchiveCount = counts.rejected + counts.archived + counts.completed

  const navigationTabs = [
    { 
      id: 'catalog', 
      icon: <ion-icon name="file-tray-full"></ion-icon>, 
      label: 'Каталог', 
      count: totalCatalogCount, 
      isActive: false 
    },
    { 
      id: 'views', 
      icon: <ion-icon name="eye"></ion-icon>, 
      label: 'Просмотры', 
      count: totalReviewsCount, 
      isActive: false 
    },
    { 
      id: 'inwork', 
      icon: <ion-icon name="build"></ion-icon>, 
      label: 'В работе', 
      count: totalWorkCount, 
      isActive: false 
    },
    { 
      id: 'archive', 
      icon: <ion-icon name="folder"></ion-icon>, 
      label: 'Архив', 
      count: totalArchiveCount, 
      isActive: false 
    }
  ]

  const handleNavigationClick = (categoryId: string) => {
    // 🔄 Переходим на главную страницу с правильной категорией и первой подвкладкой
    logger.log(`🧭 Навигация из детального просмотра в категорию: ${categoryId}`)
    
    // Получаем первую подвкладку из конфигурации категории
    const category = categoryId as OrderCategory
    const firstSubTab = CATEGORY_CONFIG[category]?.subTabs[0]?.key || 'new'
    
    navigate(`/orders?category=${categoryId}&subTab=${firstSubTab}`)
  }

  const handleLogout = () => {
    if (confirm('Вы уверены, что хотите выйти из системы?')) {
      logger.log('🚪 Выполняется выход из системы...')
      
      // Импортируем и вызываем logout
      import('../../shared/auth/initAuth').then(({ logout }) => {
        logout()
        logger.log('🔄 Перенаправление на страницу входа...')
        // Очищаем историю навигации и перенаправляем на login без state
        window.history.replaceState(null, '', '/login')
        window.location.href = '/login'
      }).catch((error) => {
        logger.error('Ошибка при выходе:', error)
        // Фаллбек - просто очищаем localStorage
        localStorage.clear()
        window.history.replaceState(null, '', '/login')
        window.location.href = '/login'
      })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Верхнее меню с профилем */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button 
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <ArrowLeft size={16} className="mr-2" />
            Назад к списку заказов
          </button>
          
          <div className="flex items-center space-x-4">
            <NotificationBell />
            <ProfileDropdown onLogout={handleLogout} />
          </div>
        </div>
      </div>



      <div className="max-w-6xl mx-auto p-6">
        {/* Заголовок заказа */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">{title}</h1>
            <div className="flex items-center">
              <span 
                className="text-sm font-semibold px-4 py-2 rounded-full"
                style={{ 
                  backgroundColor: statusStyle.bg, 
                  color: statusStyle.text 
                }}
              >
                {getStatusLabel(status)}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Левая колонка - Превью и описание */}
          <div className="lg:col-span-2 space-y-6">
            {/* Превью изображения */}
            {previewUrl && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Превью заказа</h2>
                <div className="relative">
                  <img 
                    src={previewUrl} 
                    alt="Превью заказа" 
                    className="w-full max-h-96 object-contain bg-white rounded-lg cursor-pointer transition-transform hover:scale-[1.02]"
                    onClick={() => setLightboxOpen(true)}
                    onError={(e: any) => { 
                      e.currentTarget.onerror = null
                      e.currentTarget.src = 'https://placehold.co/800x600/ffffff/9ca3af?text=Изображение+не+найдено' 
                    }}
                  />
                </div>
                
                {/* Виды обработки под фото */}
                {processingTypes && processingTypes.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Виды обработки</h3>
                    <div className="flex flex-wrap gap-2">
                      {processingTypes.map((type, i) => (
                        <span 
                          key={i} 
                          className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full border border-blue-200"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Описание заказа */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Описание заказа</h2>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="space-y-3">
                  <div className="text-gray-700 leading-relaxed">
                    • <span className="font-medium">Количество:</span> {quantity || files?.length || 0} шт.
                  </div>
                  {details.map((detail, i) => (
                    <div key={i} className="text-gray-700 leading-relaxed">
                      • {detail}
                    </div>
                  ))}
                </div>
              </div>
            </div>


          </div>

          {/* Правая колонка - Файлы и действия */}
          <div className="space-y-6">
            {/* Файлы и чертежи */}
            {files && files.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Файлы и чертежи</h2>
                
                <div className="space-y-2 mb-6">
                  {files.map((file) => (
                    <div key={file.name} className="relative">
                      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            {getFileIcon(file.type, file.name)}
                          </div>
                          <div>
                            <div className="font-medium text-sm text-gray-800">{file.name}</div>
                            <div className="text-xs text-gray-500">{file.size}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {canPreviewFile(file.type, file.name) && (
                            <button 
                              onClick={() => onPreviewFile?.(file.name)}
                              className="text-sm font-medium text-blue-600 hover:underline"
                            >
                              Просмотр
                            </button>
                          )}
                          <button 
                            onClick={() => setShowFileActions(showFileActions === file.name ? null : file.name)}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                          >
                            <EllipsisVertical size={16} className="text-gray-500" />
                          </button>
                        </div>
                        
                        {showFileActions === file.name && (
                          <div className="absolute top-12 right-0 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20 min-w-[150px]">
                            <button 
                              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                              onClick={() => {
                                onDownloadFile?.(file.name)
                                setShowFileActions(null)
                              }}
                            >
                              <CloudDownload size={16} className="mr-2" /> 
                              Скачать
                            </button>
                            
                            {/* Разделитель */}
                            <div className="border-t border-gray-100 my-1"></div>
                            
                            <button 
                              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                              onClick={() => handleEmailSingle(file.name)}
                            >
                              <Mail size={16} className="mr-2" /> 
                              На почту
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Отправить все файлы */}
                <div className="border-t border-gray-200 pt-3">
                  <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 sm:space-x-3">
                    <span className="text-xs font-medium text-gray-800">Все файлы:</span>
                    <div className="flex space-x-2">
                      <button 
                        className="flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full font-medium text-xs hover:bg-gray-200 transition-colors"
                        onClick={onDownloadAll}
                      >
                        <CloudDownload size={14} className="mr-1" /> 
                        Скачать
                      </button>
                      <button 
                        className="flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full font-medium text-xs hover:bg-gray-200 transition-colors"
                        onClick={handleEmailAll}
                      >
                        <Mail size={14} className="mr-1" /> 
                        На почту
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Информация о поданной цене */}
            {(status === 'price_given' || status === 'pending_confirmation' || status === 'executing' || 
              (status === 'defect' && submittedPrice) || 
              (status === 'completed' && submittedPrice) || 
              (status === 'rejected' && submittedPrice)) && (
              <>
                {/* Показываем ConfirmationCard только для статуса pending_confirmation */}
                {status === 'pending_confirmation' && showConfirmationCard && confirmationState && (
                  <ConfirmationCard
                    price={submittedPrice}
                    deliveryTime={submittedDeliveryTime}
                    comment={submittedComment}
                    priceFormatted={priceFormatted}
                    deliveryTimeFormatted={deliveryTimeFormatted}
                    state={confirmationState}
                    isLoading={false} // Состояние загрузки будет управляться из OrderDetailPage
                    onAccept={onAcceptConfirmation || (() => {})}
                    onModify={onRequestPriceChange || (() => {})}
                    // Передаем информацию о дедлайне
                    deadlineFormatted={deadlineFormatted}
                    deadlineStatus={deadlineStatus}
                    deadlineCssClass={deadlineCssClass}
                    daysRemaining={daysRemaining}
                    // Передаем статус заказа
                    status={status}
                  />
                )}
                
                {/* Показываем SubmittedPriceInfo для остальных статусов или когда карточка подтверждения не нужна */}
                {(status !== 'pending_confirmation' || !showConfirmationCard) && (
                  <SubmittedPriceInfo
                    price={submittedPrice}
                    deliveryTime={submittedDeliveryTime}
                    comment={submittedComment}
                    priceFormatted={priceFormatted}
                    deliveryTimeFormatted={deliveryTimeFormatted}
                    status={status}
                    // Передаем информацию о дедлайне
                    deadlineFormatted={deadlineFormatted}
                    deadlineStatus={deadlineStatus}
                    deadlineCssClass={deadlineCssClass}
                    daysRemaining={daysRemaining}
                  />
                )}
              </>
            )}

            {/* Информация об отказе */}
            {status === 'rejected' && rejectedReason && (
              <div className="bg-red-50 rounded-xl shadow-sm p-3 sm:p-4 mb-4 border border-red-200 transition-all duration-200 hover:shadow-md">
                <div className="flex justify-start items-center mb-3">
                  <h3 className="text-base sm:text-lg font-bold text-red-800 flex items-center">
                    
                    Причина отказа:
                  </h3>
                </div>
                
                <div className="text-red-600 text-sm sm:text-base bg-red-100 rounded-lg p-3 border border-red-200">
                  {rejectedReason}
                </div>
              </div>
            )}

            {/* Действия с заказом */}
            {(() => {
              const availableActions = getAvailableOrderActions(status)
              
              // Скрываем весь блок "Действия", если нет доступных действий
              if (availableActions.length === 0) {
                return null
              }
              
              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Действия</h2>
                  <div className="space-y-3">
                    {(() => {

                  // Создаем маппинг обработчиков
                  const actionHandlers: Record<string, (() => void) | undefined> = {
                    onTake,
                    onReject,
                    onRestore,
                    onSubmitPrice,
                    onEditPrice,
                    onComplete,
                    onDefect,
                    onFix,
                  }

                  return availableActions.map((action) => {
                    const handler = actionHandlers[action.handler]
                    if (!handler) return null

                    return (
                      <button
                        key={action.id}
                        className={getActionButtonClasses(action.variant, false)}
                        onClick={handler}
                      >
                        {action.icon} {action.label}
                      </button>
                    )
                    })
                  })()}
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Закрытие выпадающих меню при клике вне их */}
      {showFileActions && (
        <div 
          className="fixed inset-0 z-10" 
          onClick={() => setShowFileActions(null)}
        />
      )}

      {/* Lightbox для просмотра изображения */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        plugins={[Zoom, Fullscreen]}
        slides={previewUrl ? [
          {
            src: previewUrl,
            alt: title,
          }
        ] : []}
        styles={{
          container: { 
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(20px)"
          }
        }}
        carousel={{
          finite: true,
          preload: 2,
          padding: "16px",
          spacing: "30%",
          imageFit: "contain"
        }}
        controller={{
          closeOnBackdropClick: true,
          closeOnPullDown: true,
          closeOnPullUp: true,
          touchAction: "pan-y"
        }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true
        }}
      />

      {/* Плавающая навигация как в каталоге */}
      <FloatingNavigation 
        tabs={navigationTabs}
        onTabClick={handleNavigationClick}
      />

      {/* Email Modal */}
      <EmailModal
        isOpen={emailModal.isOpen}
        onClose={closeEmailModal}
        orderId={orderId}
        filename={emailModal.filename}
        filesCount={emailModal.filesCount}
      />
    </div>
  )
}

export default OrderDetailDesktop
